import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { uploadFile } from './files';
import { supabase } from './supabase';
import { RECEIPT_MIME_OR, RECEIPT_KIND_OR, STICKER_MIME, isReceiptMime } from './receipts';
import { cn } from './ui/Table';
import { DataGrid, GridCol } from './ui/DataGrid';
import { PickerSelect } from './ui/PickerSelect';
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { Checkbox } from './ui/arc/checkbox';
import { EmptyState } from './ui/arc/empty-state';
import { Dialog, DialogContent } from './ui/arc/dialog';
import { ChipGroup } from './ui/arc/chip-group';
import { Textarea } from './ui/arc/textarea';
import SegmentedControl from './ui/arc/segmented-control';
import { OrderPeek } from './OrderPeek';
import { PageHeader, StatCard, Notice, Panel, ScreenSkeleton } from './costs/plata-kit';
import {
    Upload, Check, CheckCheck, CheckCircle2, X, Landmark, Loader2, Paperclip, Undo2, Copy, Ban, UserCheck, ScanText, RefreshCw,
} from 'lucide-react';
import {
    Credit, TOrder, Match, ReceiptInfo, docKey, candidatePairs, bestPairs, safePairs, dayOf, isPagoNube, orderDoc, payerDoc, payerInfo,
} from './transferMatch';

// Control de transferencias (rediseño 10/10/2026).
//
// Circuito real: el cliente manda el comprobante → se reenvía a la financiera → la financiera
// lo acredita en la cuenta corriente → nos pasa el estado de cuenta. Lo ÚNICO que se controla
// acá es que cada venta cobrada por transferencia aparezca como CRÉDITO en ese estado de cuenta
// (que no se "coman" ninguna). Débitos (comisión 3%, cambio de moneda, pagos) ya no se cargan.
//
// Una venta está:
//   acreditada  → tiene un crédito asignado
//   falta       → pagada, dentro del período que cubre el estado de cuenta, sin crédito  ← la alarma
//   esperando   → pagada después del último estado de cuenta (todavía no puede estar)
//   fuera       → no pasa por la financiera (transfer_excluded_reason: pagó a otra cuenta…)
// Pago Nube "transferencia" no entra: la cobra Pago Nube, nunca aparece en este extracto.
// El cruce (DNI → monto exacto → ±2%) vive en src/transferMatch.ts.

// enrich: id de un crédito YA cargado sin CUIT (vino de una captura o con la descripción cortada)
// al que este renglón le completa la descripción.
// fp = huella para no duplicar. Dentro de UN archivo, dos renglones idénticos (mismo día, mismo
// monto, descripción cortada sin hora) son DOS transferencias: la 2da lleva '#2', la 3ra '#3'…
// Re-subir el mismo archivo da las mismas huellas → se detecta igual.
// ref = número de comprobante del renglón del extracto ('Cble 170585'). Si está, la huella es
// monto + fecha + comprobante (única de verdad). legacy = huella vieja (descripción+monto, con #n),
// para reconocer lo cargado antes de que existiera ref.
type ParsedMov = { date: string; amount: number; description: string; include: boolean; dup?: boolean; enrich?: number; fp: string; legacy: string; ref: string | null };
type Tab = 'faltan' | 'creditos' | 'acreditadas' | 'fuera';
type Row = TOrder & {
    contact_phone: string | null; receipt_urls: string[] | null; transfer_verified: boolean | null;
    transfer_excluded_at: string | null; transfer_resent_at: string | null; paid_at: string | null; _bucket: string;
};

// días de gracia entre la venta y el cierre del estado de cuenta (el 94% se acredita el mismo día)
const GRACE_DAYS = 3;

// Con centavos cuando los hay (igual que la card de la orden): redondear mostraba $57.602 para una
// orden de $57.602,19 y parecía que el total no coincidía (12/10/2026).
const fmtARS = (n: number) => '$' + (Math.round(n * 100) / 100).toLocaleString('es-AR', { minimumFractionDigits: Number.isInteger(Math.round(n * 100) / 100) ? 0 : 2, maximumFractionDigits: 2 });
const fmtDate = (d: string) => new Date(dayOf(d) + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
const fmtDiff = (m: Match) => {
    if (Math.abs(m.diff) <= 1) return 'mismo monto';
    const pct = Math.abs(m.diff) / m.amount * 100;
    return `${m.diff > 0 ? '+' : '−'}${fmtARS(Math.abs(m.diff))}${pct >= 0.1 ? ` (${pct.toFixed(1).replace('.', ',')}%)` : ''}`;
};
const addDays = (d: string, n: number) => { const x = new Date(d + 'T12:00:00'); x.setDate(x.getDate() + n); return dayOf(x.toISOString()); };
const monthLabel = (k: string) => new Date(k + '-15T12:00:00').toLocaleDateString('es-AR', { month: 'long' }).replace(/^./, c => c.toUpperCase());

// ID natural de un crédito para no cargarlo dos veces (la descripción trae fecha+hora+banco+CUIT).
const canonKey = (date: string, amount: number | string, desc: string) => {
    const d = String(desc || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const a = String(Number(amount));
    return /\d{2}\/\d{2}\/\d{2}/.test(d) ? `in|${d}|${a}` : `in|${date}|${d}|${a}`;
};

function displayId(o: Pick<TOrder, 'source' | 'tiendanube_id' | 'id'>): string {
    if (o.source === 'woocommerce' && o.tiendanube_id) return `#BT-${String(o.tiendanube_id).replace(/^(WC-|BT-)/i, '')}`;
    if ((o.source === 'tiendanube' || o.source === 'mercadolibre') && o.tiendanube_id) return `#${o.tiendanube_id}`;
    return `K-${o.id}`;
}

// Venta manual cobrada a la cuenta de Krilic (se elige en el popup de venta): no pasa por la
// financiera, así que no se busca en su estado de cuenta.
const isKrilic = (o: TOrder) => o.payment_details?.transfer_account === 'krilic';
const KRILIC_REASON = 'Transferencia a la cuenta de Krilic (elegido en la venta)';

const KIND_LABEL: Record<Match['kind'], string> = { comprobante: 'coincide con el comprobante', dni: 'mismo DNI', exacto: 'mismo monto', aprox: 'monto parecido' };

// Lectura de un comprobante (tabla transfer_receipts, la escribe la edge function read-receipt)
type ReceiptRead = {
    id: number; media_url: string; order_id: number | null; amount: number | null; tx_date: string | null; tx_time: string | null;
    payer_name: string | null; payer_doc: string | null; payer_bank: string | null; dest_name: string | null; dest_account: string | null;
    dest_ok: boolean | null; operation_id: string | null; is_receipt: boolean | null; notes: string | null; error: string | null;
};
const fmtCuit = (d: string | null) => d && d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : (d || '');
const EXCLUDE_REASONS = [
    { value: 'Pagó a otra cuenta (Mercado Pago / personal)', label: 'Pagó a otra cuenta' },
    { value: 'Al final pagó en efectivo', label: 'Pagó en efectivo' },
    { value: 'Pagó con tarjeta o link de pago', label: 'Pagó con tarjeta / link' },
    { value: 'Venta duplicada o de prueba', label: 'Duplicada o de prueba' },
    { value: 'Otro', label: 'Otro' },
];

export function Conciliacion({ getBucket }: { getBucket: (o: any) => string }) {
    const [credits, setCredits] = useState<Credit[]>([]);
    const [orders, setOrders] = useState<Row[]>([]);
    const [reviewedUntil, setReviewedUntil] = useState('');
    const [mediaByTail, setMediaByTail] = useState<Map<string, any[]>>(new Map());
    const [reads, setReads] = useState<ReceiptRead[]>([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState<Tab>('faltan');
    const [month, setMonth] = useState('all');
    const [busy, setBusy] = useState<string | null>(null);
    const [notice, setNotice] = useState<{ tone: 'success' | 'danger' | 'warning' | 'info'; text: string } | null>(null);
    // carga del estado de cuenta
    const fileRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [parsed, setParsed] = useState<ParsedMov[]>([]);
    const [saving, setSaving] = useState(false);
    // "no pasa por la financiera"
    const [excluding, setExcluding] = useState<Row | null>(null);
    const [peek, setPeek] = useState<Row | null>(null); // orden abierta en el popup (OrderPeek)
    const [exReason, setExReason] = useState(EXCLUDE_REASONS[0].value);
    const [exNote, setExNote] = useState('');

    const fetchAll = useCallback(async () => {
        const since = new Date(); since.setDate(since.getDate() - 200);
        const [credRes, ordRes, setRes, mediaRes, readRes] = await Promise.all([
            supabase.from('bank_movements').select('id, movement_date, amount, description, status, order_id')
                .eq('direction', 'in').order('movement_date', { ascending: false }).limit(5000),
            supabase.from('orders')
                .select('id, tiendanube_id, source, contact_name, contact_phone, total_price, deposit_amount, created_at, status, payment_status, gateway_name, payment_details, customer_doc, transfer_excluded_reason, transfer_excluded_at, transfer_resent_at, transfer_verified, receipt_urls, paid_at')
                .gte('created_at', since.toISOString()).is('deleted_at', null)
                .not('status', 'in', '(presupuesto,stock)')
                .order('created_at', { ascending: false }).limit(3000),
            supabase.from('cost_settings').select('bank_reviewed_until').eq('id', 1).single(),
            // comprobantes: adjuntos ENTRANTES de WhatsApp que son imagen o PDF (ver src/receipts.ts)
            supabase.from('whatsapp_logs').select('phone, media_url, media_mime_type, created_at')
                .eq('direction', 'incoming').not('media_url', 'is', null)
                .or(RECEIPT_MIME_OR).or(RECEIPT_KIND_OR).neq('media_mime_type', STICKER_MIME)
                .gte('created_at', since.toISOString()).order('created_at', { ascending: false }).limit(2000),
            supabase.from('transfer_receipts').select('*').order('created_at', { ascending: false }).limit(3000),
        ]);
        setReads(((readRes.data || []) as any[]).map(r => ({ ...r, amount: r.amount == null ? null : Number(r.amount) })));
        const byTail = new Map<string, any[]>();
        for (const r of (mediaRes.data || [])) {
            if (!isReceiptMime(r.media_mime_type)) continue;
            const t = String(r.phone || '').replace(/\D/g, '').slice(-8);
            if (!t) continue;
            if (!byTail.has(t)) byTail.set(t, []);
            byTail.get(t)!.push(r);
        }
        setMediaByTail(byTail);
        setCredits(((credRes.data || []) as any[]).map(c => ({ ...c, amount: Number(c.amount) })));
        // transferencias + efectivo (el efectivo solo sirve para el selector manual: clientes que
        // eligieron "a convenir" y terminaron transfiriendo)
        // + las órdenes que tienen un crédito asignado aunque no sean "transferencia" (canceladas, Pago
        // Nube, efectivo, de hace más de 200 días): si no, ese crédito quedaba invisible (Laura Castro,
        // #4078 cancelada, 12/10/2026).
        const ordRows = ((ordRes.data || []) as any[]);
        const have = new Set(ordRows.map(o => o.id));
        const missing = Array.from(new Set(((credRes.data || []) as any[]).filter(c => c.order_id && !have.has(c.order_id)).map(c => c.order_id)));
        if (missing.length) {
            const { data: extra } = await supabase.from('orders')
                .select('id, tiendanube_id, source, contact_name, contact_phone, total_price, deposit_amount, created_at, status, payment_status, gateway_name, payment_details, customer_doc, transfer_excluded_reason, transfer_excluded_at, transfer_resent_at, transfer_verified, receipt_urls, paid_at')
                .in('id', missing);
            ordRows.push(...(extra || []).map((o: any) => ({ ...o, _withCredit: true })));
        }
        setOrders(ordRows
            .map(o => ({ ...o, total_price: Number(o.total_price || 0), _bucket: getBucket(o) }))
            .filter(o => o._bucket === 'transferencia' || o._bucket === 'efectivo' || o._withCredit));
        setReviewedUntil((setRes.data as any)?.bank_reviewed_until || '');
        setLoading(false);
    }, [getBucket]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ── Estado de cada cosa ───────────────────────────────────────────────
    const creditsByOrder = useMemo(() => {
        const m = new Map<number, Credit[]>();
        for (const c of credits) if (c.status === 'asignado' && c.order_id) {
            if (!m.has(c.order_id)) m.set(c.order_id, []);
            m.get(c.order_id)!.push(c);
        }
        return m;
    }, [credits]);
    const ordersById = useMemo(() => new Map(orders.map(o => [o.id, o])), [orders]);

    const cover = useMemo(() => {
        const today = dayOf(new Date().toISOString());
        const ds = credits.map(c => c.movement_date).filter(d => d <= today).sort();
        if (!ds.length) return null;
        const wm = reviewedUntil && reviewedUntil <= today ? reviewedUntil : '';
        const end = wm && wm > ds[ds.length - 1] ? wm : ds[ds.length - 1];
        return { start: ds[0], end, limit: addDays(end, -GRACE_DAYS) };
    }, [credits, reviewedUntil]);

    // ventas que TIENEN que pasar por la financiera
    const transferOrders = useMemo(() => orders.filter(o => o._bucket === 'transferencia' && !isPagoNube(o)), [orders]);
    const pagoNubeCount = useMemo(() => orders.filter(o => o._bucket === 'transferencia' && isPagoNube(o) && cover && dayOf(o.created_at) >= cover.start).length, [orders, cover]);

    // ── Cuándo pagó: llegada del comprobante (WhatsApp o adjuntado a mano) o, si no hay
    //    comprobante, cuándo se marcó pagada (orders.paid_at, migr 104). ──
    const payInfo = useCallback((o: Row): { date: string; kind: 'comprobante' | 'pagada' } | null => {
        const ts: number[] = [];
        for (const u of (Array.isArray(o.receipt_urls) ? o.receipt_urls : [])) {
            const m = String(u).match(/manual_\d+_(\d{13})/); // el nombre del archivo trae el momento de la carga
            if (m) ts.push(Number(m[1]));
        }
        const tail = String(o.contact_phone || '').replace(/\D/g, '').slice(-8);
        if (tail) {
            const t0 = new Date(o.created_at).getTime() - 3600000;
            for (const r of (mediaByTail.get(tail) || [])) { const t = new Date(r.created_at).getTime(); if (t >= t0) ts.push(t); }
        }
        // La MÁS TEMPRANA entre el comprobante y "marcada paga": si se marcó pagada el 18/08 y el
        // comprobante se adjuntó recién el 12/10, pagó a más tardar el 18/08 (K-23999).
        const rec = ts.length ? Math.min(...ts) : Infinity;
        const paid = o.paid_at ? new Date(o.paid_at).getTime() : Infinity;
        if (rec === Infinity && paid === Infinity) return null;
        return rec <= paid
            ? { date: new Date(rec).toISOString(), kind: 'comprobante' }
            : { date: o.paid_at!, kind: 'pagada' };
    }, [mediaByTail]);
    const anchorOf = useCallback((o: Row) => payInfo(o)?.date || o.created_at, [payInfo]);

    const stateOf = useCallback((o: Row): 'acreditada' | 'fuera' | 'falta' | 'esperando' | null => {
        if (creditsByOrder.has(o.id)) return 'acreditada';
        if (o.transfer_excluded_reason || isKrilic(o)) return 'fuera';
        if (o.status === 'cancelada' || o.payment_status !== 'paid' || !cover) return null;
        const d = dayOf(anchorOf(o));
        if (dayOf(o.created_at) < cover.start && d < cover.start) return null;
        return d <= cover.limit ? 'falta' : 'esperando';
    }, [creditsByOrder, cover, anchorOf]);

    const inMonth = useCallback((d: string) => month === 'all' || dayOf(d).slice(0, 7) === month, [month]);
    const months = useMemo(() => {
        if (!cover) return [];
        const out: string[] = [];
        for (let k = cover.start.slice(0, 7); k <= cover.end.slice(0, 7);) {
            out.push(k);
            const [y, m] = k.split('-').map(Number);
            k = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
        }
        return out;
    }, [cover]);

    const groups = useMemo(() => {
        const g = { acreditada: [] as Row[], fuera: [] as Row[], falta: [] as Row[], esperando: [] as Row[] };
        for (const o of transferOrders) {
            const s = stateOf(o);
            if (s && inMonth(o.created_at)) g[s].push(o);
        }
        // ventas FUERA del control (efectivo, Pago Nube, tarjeta) que igual tienen un crédito asignado:
        // van a Acreditadas con aviso, para poder revisarlas o deshacer
        const inTransfer = new Set(transferOrders.map(o => o.id));
        for (const o of orders) if (!inTransfer.has(o.id) && creditsByOrder.has(o.id) && inMonth(o.created_at)) g.acreditada.push(o);
        return g;
    }, [transferOrders, orders, creditsByOrder, stateOf, inMonth]);

    const freeCredits = useMemo(() => credits.filter(c => c.status === 'pendiente'), [credits]);
    const ignoredCredits = useMemo(() => credits.filter(c => c.status === 'ignorado' && inMonth(c.movement_date)), [credits, inMonth]);

    // Sugerencias: todas las ventas sin crédito (pagadas o no, canceladas, efectivo) contra los
    // créditos libres; cada venta y cada crédito se sugieren una sola vez.
    const openOrders = useMemo(() => orders.filter(o => !creditsByOrder.has(o.id) && !o.transfer_excluded_reason && !isKrilic(o)
        && !isPagoNube(o) && (o._bucket === 'transferencia' || !o.transfer_verified)), [orders, creditsByOrder]);
    const readsByOrder = useMemo(() => {
        const m = new Map<number, ReceiptRead[]>();
        for (const r of reads) if (r.order_id) { if (!m.has(r.order_id)) m.set(r.order_id, []); m.get(r.order_id)!.push(r); }
        return m;
    }, [reads]);
    const receiptInfo = useCallback((id: number): ReceiptInfo[] => (readsByOrder.get(id) || [])
        .filter(r => r.is_receipt !== false && !r.error)
        .map(r => ({ amount: r.amount, date: r.tx_date, doc: r.payer_doc })), [readsByOrder]);
    const pairs = useMemo(() => candidatePairs(openOrders.map(o => ({ ...o, receipts: receiptInfo(o.id), pay_date: payInfo(o)?.date || null })), freeCredits), [openOrders, freeCredits, receiptInfo, payInfo]);
    const best = useMemo(() => bestPairs(pairs), [pairs]);
    // seguras: solo ventas por transferencia pagadas y no canceladas
    const safe = useMemo(() => {
        const ok = new Set(transferOrders.filter(o => o.payment_status === 'paid' && o.status !== 'cancelada').map(o => o.id));
        return safePairs(pairs.filter(p => ok.has(p.orderId)));
    }, [pairs, transferOrders]);

    const freeInMonth = useMemo(() => freeCredits.filter(c => inMonth(c.movement_date)), [freeCredits, inMonth]);

    // ── Escrituras (actualización local, sin recargar) ────────────────────
    const setOrderPatch = (id: number, patch: Partial<Row>) => setOrders(os => os.map(o => o.id === id ? { ...o, ...patch } : o));
    const setCreditPatch = (id: number, patch: Partial<Credit>) => setCredits(cs => cs.map(c => c.id === id ? { ...c, ...patch } : c));

    const assign = async (orderId: number, creditId: number) => {
        const now = new Date().toISOString();
        const r1 = await supabase.from('bank_movements').update({ status: 'asignado', order_id: orderId, assigned_at: now }).eq('id', creditId);
        if (r1.error) throw r1.error;
        await supabase.from('orders').update({ transfer_verified: true, transfer_verified_at: now }).eq('id', orderId);
        setCreditPatch(creditId, { status: 'asignado', order_id: orderId });
        setOrderPatch(orderId, { transfer_verified: true });
    };
    const run = async (key: string, fn: () => Promise<void>) => {
        setBusy(key);
        try { await fn(); } catch (e: any) { setNotice({ tone: 'danger', text: 'No se pudo guardar: ' + (e?.message || e) }); }
        setBusy(null);
    };
    const confirmSafe = (list = safe) => run('safe', async () => {
        for (const p of list) await assign(p.orderId, p.creditId);
        setNotice({ tone: 'success', text: `Se cruzaron ${list.length} transferencia${list.length === 1 ? '' : 's'} con su crédito.` });
    });
    const unassign = (c: Credit) => run(`c${c.id}`, async () => {
        const orderId = c.order_id;
        await supabase.from('bank_movements').update({ status: 'pendiente', order_id: null, assigned_at: null }).eq('id', c.id);
        setCreditPatch(c.id, { status: 'pendiente', order_id: null });
        const o = orderId ? ordersById.get(orderId) : null;
        const others = orderId ? (creditsByOrder.get(orderId) || []).filter(x => x.id !== c.id).length : 0;
        if (o && !others && !o.transfer_excluded_reason) {
            await supabase.from('orders').update({ transfer_verified: false, transfer_verified_at: null }).eq('id', o.id);
            setOrderPatch(o.id, { transfer_verified: false });
        }
    });
    // "No es una venta" en un crédito que COINCIDE con una venta (comprobante, DNI, monto exacto o
    // parecido en fecha) pide confirmar: un crédito ignorado no se sugiere nunca más y esa venta queda como
    // faltante aunque la plata entró (caso #4354, $633.250 ignorado en septiembre).
    const confirmIgnore = (c: Credit) => {
        const m = pairs.filter(p => p.creditId === c.id)
            .map(p => ordersById.get(p.orderId)).filter(Boolean) as Row[];
        if (m.length) {
            const list = m.slice(0, 3).map(o => `• ${displayId(o)} ${o.contact_name || ''} — ${fmtARS(o.total_price)} (${fmtDate(o.created_at)})`).join('\n');
            if (!window.confirm(`Este crédito de ${fmtARS(c.amount)} del ${fmtDate(c.movement_date)} coincide con:\n${list}\n\nSi lo marcás como "No es una venta" no se va a sugerir más y esa venta va a figurar como faltante. ¿Seguro que no es una venta?`)) return;
        }
        ignore(c);
    };
    const ignore = (c: Credit, back = false) => run(`c${c.id}`, async () => {
        await supabase.from('bank_movements').update({ status: back ? 'pendiente' : 'ignorado' }).eq('id', c.id);
        setCreditPatch(c.id, { status: back ? 'pendiente' : 'ignorado' });
    });
    const saveExclude = () => {
        const o = excluding; if (!o) return;
        const reason = exReason === 'Otro' ? (exNote.trim() || 'Otro') : (exNote.trim() ? `${exReason} — ${exNote.trim()}` : exReason);
        return run(`o${o.id}`, async () => {
            const now = new Date().toISOString();
            const r = await supabase.from('orders').update({ transfer_excluded_reason: reason, transfer_excluded_at: now, transfer_verified: true, transfer_verified_at: now }).eq('id', o.id);
            if (r.error) throw r.error;
            setOrderPatch(o.id, { transfer_excluded_reason: reason, transfer_excluded_at: now, transfer_verified: true });
            setExcluding(null);
        });
    };
    // check "reenviado": el comprobante se le volvió a mandar a la financiera por WhatsApp
    const toggleResent = (o: Row) => run(`r${o.id}`, async () => {
        const v = o.transfer_resent_at ? null : new Date().toISOString();
        const r = await supabase.from('orders').update({ transfer_resent_at: v }).eq('id', o.id);
        if (r.error) throw r.error;
        setOrderPatch(o.id, { transfer_resent_at: v });
    });
    const unexclude = (o: Row) => run(`o${o.id}`, async () => {
        const has = creditsByOrder.has(o.id);
        await supabase.from('orders').update({ transfer_excluded_reason: null, transfer_excluded_at: null, transfer_verified: has, transfer_verified_at: has ? new Date().toISOString() : null }).eq('id', o.id);
        setOrderPatch(o.id, { transfer_excluded_reason: null, transfer_excluded_at: null, transfer_verified: has });
    });

    // ── Leer comprobantes con visión (read-receipt). Una lectura por archivo: queda guardada. ──
    const [reading, setReading] = useState<number | null>(null);
    const readReceipts = async (o: Row, force = false) => {
        const urls = receiptsFor(o).slice(0, 3);
        if (!urls.length) return;
        setReading(o.id);
        const got: ReceiptRead[] = [];
        for (const url of urls) {
            const { data, error } = await supabase.functions.invoke('read-receipt', { body: { media_url: url, order_id: o.id, force } });
            if (error || !data?.ok) { setNotice({ tone: 'danger', text: 'No se pudo leer el comprobante: ' + (data?.error || error?.message || 'error') }); break; }
            got.push({ ...data.receipt, amount: data.receipt.amount == null ? null : Number(data.receipt.amount) });
        }
        if (got.length) setReads(rs => [...got, ...rs.filter(r => !got.some(g => g.media_url === r.media_url))]);
        setReading(null);
    };

    // ── Comprobantes de una venta (mismo criterio que la card) ────────────
    const receiptsFor = useCallback((o: Row): string[] => {
        const manual = Array.isArray(o.receipt_urls) ? o.receipt_urls : [];
        const tail = String(o.contact_phone || '').replace(/\D/g, '').slice(-8);
        if (!tail) return manual;
        const t0 = new Date(o.created_at).getTime() - 3600000;
        return [...manual, ...(mediaByTail.get(tail) || []).filter(m => new Date(m.created_at).getTime() >= t0).map(m => m.media_url)];
    }, [mediaByTail]);

    // ── Carga del estado de cuenta (solo CRÉDITOS) ────────────────────────
    const onFiles = async (fileList: FileList | null) => {
        if (!fileList || !fileList.length) return;
        // Copiar YA: el input se vacía (value = '') apenas vuelve el onChange y la FileList es
        // "viva" — después del primer await quedaba vacía y la subida no leía nada (12/10/2026).
        const files = Array.from(fileList);
        setParsing(true); setNotice(null);
        const all: ParsedMov[] = [];
        let skippedOut = 0, skippedFuture = 0;
        const today = dayOf(new Date().toISOString());
        // "revisado hasta" FRESCO de la base: una pestaña abierta hace rato tiene el viejo en memoria
        // y mandaba un corte equivocado (10/10/2026: quedaron afuera los créditos del 01/10).
        const { data: wm } = await supabase.from('cost_settings').select('bank_reviewed_until').eq('id', 1).single();
        const since: string = (wm as any)?.bank_reviewed_until || '';
        setReviewedUntil(since);
        try {
            for (const file of files) {
                const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
                // a Storage y se manda el path: en base64 los PDF grandes superan el límite del gateway
                const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
                // R2 privado (krilic-private) desde el 02/11/2026; parse-bank-movements lo lee y lo borra
                try { await uploadFile('bank-uploads', path, file, isPdf ? 'application/pdf' : (file.type || 'image/png')); }
                catch (e: any) { throw new Error('No se pudo subir el archivo: ' + (e?.message || e)); }
                const { data, error } = await supabase.functions.invoke('parse-bank-movements', {
                    // PDF: se lee entero (sin corte por fecha); lo repetido se reconoce por Cble abajo.
                    // Imágenes (lectura con IA): el corte ahorra tiempo y costo.
                    body: { path, media_type: isPdf ? 'application/pdf' : (file.type || 'image/png'), since: isPdf ? undefined : (since || undefined) },
                });
                if (error || !data?.ok) throw new Error(data?.error || error?.message || 'No se pudo leer el archivo');
                const occ = new Map<string, number>();
                for (const m of (data.movements || [])) {
                    if (m.direction !== 'in') { skippedOut++; continue; }
                    // "TT-Com" en el haber = devolución de comisión de la financiera, no una venta
                    if (/^\s*TT-Com\b/i.test(m.description || '')) { skippedOut++; continue; }
                    // fecha futura = renglón mal leído (fecha valor, saldo…): nunca es un crédito de hoy
                    if (m.date > today) { skippedFuture++; continue; }
                    const key = canonKey(m.date, Number(m.amount), m.description);
                    const n = (occ.get(key) || 0) + 1; occ.set(key, n);
                    const legacy = n > 1 ? `${key}#${n}` : key;
                    const ref = m.ref ? String(m.ref) : null;
                    all.push({ date: m.date, amount: Number(m.amount), description: m.description, include: true, legacy, ref, fp: ref ? `in|${m.date}|${Number(m.amount)}|cble:${ref}` : legacy });
                }
                if (data.truncated) setNotice({ tone: 'warning', text: 'El archivo era muy largo y la lectura se cortó. Se tomaron las filas completas: subilo de nuevo para traer el resto (lo ya cargado se saltea solo).' });
            }
            // Lo ya cargado, fresco de la base (con Cble y huella), alrededor de las fechas del archivo
            const minDate = all.reduce((mn, p) => p.date < mn ? p.date : mn, '9999-12-31');
            const { data: exRows } = await supabase.from('bank_movements')
                .select('id, movement_date, amount, description, bank_ref, fingerprint')
                .eq('direction', 'in').gte('movement_date', addDays(minDate === '9999-12-31' ? '2000-01-01' : minDate, -15)).limit(5000);
            const ex = ((exRows || []) as any[]).map(r => ({ ...r, amount: Number(r.amount), description: String(r.description || '') }));
            const exRefs = new Set(ex.map(r => r.bank_ref).filter(Boolean));
            const exFp = new Set(ex.map(r => r.fingerprint).filter(Boolean));
            const claimed = new Set<number>();
            const seen = new Set<string>();
            // descripción que vino rota de lectores viejos ("1/10/2026", "CREDITO=45.376,00", "TT-Trn" pelado)
            const broken = (d: string) => !d || /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(d) || /^CREDITO=/.test(d) || /^TT-Trn$/i.test(d);
            const dayDiff = (a: string, b: string) => Math.abs(new Date(a + 'T12:00:00').getTime() - new Date(b + 'T12:00:00').getTime()) / 86400000;
            for (const p of all) {
                // 1) mismo Cble = misma transferencia (código único de la financiera, pedido 12/10)
                if (p.ref && exRefs.has(p.ref)) { p.dup = true; p.include = false; seen.add(p.fp); continue; }
                // 2) el MISMO renglón cargado antes sin Cble o con la descripción incompleta: mismo monto y
                //    (misma huella, o misma fecha y su descripción es el comienzo de esta, o descripción rota
                //    a ≤10 días). Cada crédito cargado se reconoce UNA vez: dos transferencias iguales del
                //    mismo día siguen siendo dos.
                const c = ex.filter(r => !claimed.has(r.id) && Math.abs(r.amount - p.amount) < 0.01 && (
                    r.fingerprint === p.legacy || r.fingerprint === p.fp
                    || (r.movement_date === p.date && (r.description === p.description || p.description.startsWith(r.description)))
                    || (broken(r.description) && dayDiff(r.movement_date, p.date) <= 10)))
                    .sort((x, y) => dayDiff(x.movement_date, p.date) - dayDiff(y.movement_date, p.date))[0];
                if (c) {
                    claimed.add(c.id); p.dup = true; p.include = false;
                    // se le completa lo que le falta: Cble, CUIT / descripción completa, fecha real
                    if ((p.ref && !c.bank_ref) || p.description.length > c.description.length) p.enrich = c.id;
                    seen.add(p.fp); continue;
                }
                // 3) repetido dentro de la misma tanda (mismo archivo subido dos veces)
                if (exFp.has(p.fp) || seen.has(p.fp)) { p.dup = true; p.include = false; }
                seen.add(p.fp);
            }
            if (!all.length) setNotice({ tone: 'info', text: since ? `No hay créditos nuevos posteriores al ${fmtDate(since)}: ya está todo cargado.` : 'No se encontraron créditos en el archivo.' });
            else if (skippedFuture) setNotice({ tone: 'warning', text: `Se descartaron ${skippedFuture} renglón${skippedFuture === 1 ? '' : 'es'} con fecha futura (no pueden ser créditos todavía).` });
            setParsed(prev => [...prev, ...all]);
        } catch (e: any) {
            setNotice({ tone: 'danger', text: String(e?.message || e) });
        }
        setParsing(false);
    };

    const loadParsed = async () => {
        const rows = parsed.filter(p => p.include);
        const enrich = parsed.filter(p => p.enrich);
        if (!rows.length && !enrich.length) return;
        setSaving(true);
        for (const p of enrich) {
            await supabase.from('bank_movements').update({ description: p.description, fingerprint: p.fp, bank_ref: p.ref, movement_date: p.date }).eq('id', p.enrich!);
        }
        const seen = new Set<string>();
        const inserts: any[] = [];
        for (const p of rows) {
            if (seen.has(p.fp)) continue;
            seen.add(p.fp);
            inserts.push({ movement_date: p.date, amount: p.amount, description: p.description, direction: 'in', fingerprint: p.fp, bank_ref: p.ref });
        }
        const { error } = inserts.length ? await supabase.from('bank_movements').upsert(inserts, { onConflict: 'fingerprint', ignoreDuplicates: true }) : { error: null };
        if (error) { setNotice({ tone: 'danger', text: 'No se pudo guardar: ' + error.message }); setSaving(false); return; }
        // "Revisado hasta" = la transferencia más nueva del archivo (fecha del comprobante)
        const maxDate = parsed.reduce((mx, p) => p.date > mx ? p.date : mx, '');
        const { data: wm2 } = await supabase.from('cost_settings').select('bank_reviewed_until').eq('id', 1).single();
        const cur: string = (wm2 as any)?.bank_reviewed_until || '';
        if (maxDate && maxDate <= dayOf(new Date().toISOString()) && (!cur || maxDate > cur)) {
            await supabase.from('cost_settings').update({ bank_reviewed_until: maxDate }).eq('id', 1);
        }
        setParsed([]);
        setSaving(false);
        await fetchAll();
        setNotice({ tone: 'success', text: `Se cargaron ${inserts.length} crédito${inserts.length === 1 ? '' : 's'}${enrich.length ? ` y se completó quién transfirió en ${enrich.length} que ya estaban` : ''}. Revisá abajo los cruces seguros y confirmalos de una.` });
    };

    // ── Texto para reclamar a la financiera ───────────────────────────────
    const copyClaim = async () => {
        const lines = groups.falta
            .sort((a, b) => a.created_at.localeCompare(b.created_at))
            .map(o => `• ${fmtDate(o.created_at)} — ${fmtARS(o.total_price)} — ${o.contact_name || 'sin nombre'} (${displayId(o)})${o.transfer_resent_at ? ` — comprobante reenviado el ${fmtDate(o.transfer_resent_at)}` : ''}`);
        const total = groups.falta.reduce((s, o) => s + o.total_price, 0);
        const txt = `Transferencias enviadas que no figuran en el estado de cuenta (hasta el ${fmtDate(cover!.end)}):\n${lines.join('\n')}\nTotal: ${fmtARS(total)}`;
        try { await navigator.clipboard.writeText(txt); setNotice({ tone: 'success', text: `Lista copiada (${lines.length} transferencias). Pegala en el chat con la financiera junto con los comprobantes.` }); }
        catch { setNotice({ tone: 'danger', text: 'No se pudo copiar al portapapeles.' }); }
    };

    if (loading) return <div className="tw w-full min-w-0 pb-12 pt-1"><ScreenSkeleton /></div>;

    // ── KPIs ──────────────────────────────────────────────────────────────
    const sum = (rs: Row[]) => rs.reduce((s, o) => s + o.total_price, 0);
    const controlled = groups.acreditada.length + groups.falta.length;
    const pctOk = controlled ? Math.round(groups.acreditada.length / controlled * 100) : 0;
    const safeInMonth = safe.filter(p => { const o = ordersById.get(p.orderId); return o && inMonth(o.created_at); });

    // ── Celdas compartidas ────────────────────────────────────────────────
    const saleCell = (o: Row) => (
        <span className="grid min-w-0 justify-items-start gap-0.5">
            <span className="truncate font-semibold text-ink">{o.contact_name || 'sin nombre'}</span>
            <button type="button" onClick={() => setPeek(o)} title="Ver la orden"
                className="cursor-pointer text-[length:var(--text-xs)] text-[color:var(--brand)] underline-offset-2 hover:underline">
                {displayId(o)}{o.status === 'cancelada' ? ' · cancelada' : ''}
            </button>
            {creditsByOrder.has(o.id) && (o.status === 'cancelada' || isPagoNube(o) || o._bucket !== 'transferencia') && (
                <Badge size="sm" tone="warning" title="Tiene un crédito asignado pero la venta está cancelada o figura con otro medio de pago. Revisá si el crédito es de esta venta (si no, Deshacer).">
                    {o.status === 'cancelada' ? 'CANCELADA con crédito' : isPagoNube(o) ? 'figura Pago Nube' : `figura ${o._bucket}`}
                </Badge>
            )}
            {stateOf(o) === 'esperando' && (
                <Badge size="sm" tone="info" title={`Pagada después del ${cover ? fmtDate(cover.limit) : ''}: todavía no puede estar en el estado de cuenta cargado. Se controla cuando subas el próximo.`}>PRÓXIMO CIERRE</Badge>
            )}
        </span>
    );
    const receiptBtn = (o: Row) => {
        const recs = receiptsFor(o);
        if (!recs.length) return <span className="text-[length:var(--text-xs)] text-faint">sin adjunto</span>;
        const done = readsByOrder.get(o.id) || [];
        return (
            <div className="grid min-w-[200px] gap-1">
                <div className="flex flex-wrap items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => window.open(recs[0], '_blank')} title="Ver el comprobante que mandó el cliente"><Paperclip size={14} /> Ver{recs.length > 1 ? ` (${recs.length})` : ''}</Button>
                    <Button variant="secondary" size="sm" loading={reading === o.id} disabled={reading !== null && reading !== o.id}
                        onClick={() => readReceipts(o, done.length > 0)}
                        title={done.length ? 'Volver a leer el comprobante' : 'Leer el comprobante (monto, fecha, quién pagó, a qué cuenta) para cruzarlo con el banco'}>
                        {done.length ? <RefreshCw size={14} /> : <ScanText size={14} />} {done.length ? 'Releer' : 'Leer'}
                    </Button>
                </div>
                {done.map(r => <ReceiptLine key={r.id} r={r} o={o} />)}
            </div>
        );
    };
    const ReceiptLine = ({ r, o }: { r: ReceiptRead; o: Row }) => {
        if (r.error) return <span className="text-[length:var(--text-xs)] text-[var(--danger)]">{r.error}</span>;
        if (r.is_receipt === false) return <Badge size="sm" tone="warning">no es un comprobante</Badge>;
        const amountOff = r.amount != null && Math.abs(r.amount - o.total_price) > 1;
        return (
            <div className="grid gap-0.5 text-[length:var(--text-xs)] leading-snug">
                <span className="tabular-nums text-ink">
                    <b className={amountOff ? 'text-[var(--warning)]' : undefined}>{r.amount != null ? fmtARS(r.amount) : 'sin monto'}</b>
                    {r.tx_date ? ` · ${fmtDate(r.tx_date)}` : ''}{r.tx_time ? ` ${r.tx_time}` : ''}{r.payer_bank ? ` · ${r.payer_bank}` : ''}
                </span>
                <span className="truncate text-soft" title={r.payer_name || undefined}>{r.payer_name || 'sin nombre'}{r.payer_doc ? ` · ${fmtCuit(r.payer_doc)}` : ''}</span>
                <span className="flex flex-wrap gap-1">
                    {r.dest_ok === false && <Badge size="sm" tone="danger" title={`${r.dest_name || ''} ${r.dest_account || ''}`}>fue a otra cuenta: {r.dest_name || r.dest_account}</Badge>}
                    {r.dest_ok === true && <Badge size="sm" tone="success">a la financiera</Badge>}
                    {amountOff && <Badge size="sm" tone="warning">venta {fmtARS(o.total_price)}</Badge>}
                    {r.notes && <Badge size="sm" tone="warning" title={r.notes}>ojo: {r.notes.slice(0, 40)}</Badge>}
                </span>
            </div>
        );
    };

    // selector manual: créditos libres ordenados por cercanía al monto y la fecha de la venta
    // Selector manual de créditos: primero los LIBRES (más cerca en monto y fecha); al final también
    // los ya asignados a OTRA venta o marcados "No es una venta", para poder moverlos (una
    // asignación vieja equivocada dejaba el crédito inencontrable: Leandro, 12/10/2026).
    const creditScore = (o: Row, c: Credit) => Math.abs(c.amount - o.total_price) / Math.max(1, o.total_price)
        + Math.abs(new Date(c.movement_date).getTime() - new Date(anchorOf(o)).getTime()) / 86400000 / 60;
    const creditOptionsFor = (o: Row) => {
        const tags = (c: Credit) => [payerInfo(c.description).bank, payerInfo(c.description).cuit,
            payerDoc(c.description) && payerDoc(c.description) === orderDoc(o) ? 'MISMO DNI' : null,
            payerDoc(c.description) && receiptInfo(o.id).some(r => docKey(r.doc) === payerDoc(c.description)) ? 'CUIT DEL COMPROBANTE' : null,
            receiptInfo(o.id).some(r => r.amount != null && Math.abs(r.amount - c.amount) <= 1) ? 'MONTO DEL COMPROBANTE' : null];
        const free = freeCredits.map(c => ({ c, score: creditScore(o, c) })).sort((a, b) => a.score - b.score).slice(0, 200)
            .map(({ c }) => ({ value: c.id, label: `${fmtARS(c.amount)} · ${fmtDate(c.movement_date)}`, sublabel: tags(c).filter(Boolean).join(' · ') || (c.description || '') }));
        const taken = credits.filter(c => (c.status === 'asignado' && c.order_id !== o.id) || c.status === 'ignorado')
            .map(c => ({ c, score: creditScore(o, c) })).filter(x => x.score < 0.5).sort((a, b) => a.score - b.score).slice(0, 100)
            .map(({ c }) => {
                const other = c.order_id ? ordersById.get(c.order_id) : null;
                const where = c.status === 'ignorado' ? 'MARCADO "No es una venta"'
                    : `YA ASIGNADO a ${other ? `${displayId(other)} ${other.contact_name || ''}` : `orden ${c.order_id}`}`;
                return { value: c.id, label: `${fmtARS(c.amount)} · ${fmtDate(c.movement_date)} — ${where}`, sublabel: tags(c).filter(Boolean).join(' · ') || (c.description || '') };
            });
        return [...free, ...taken];
    };
    // Elegir un crédito: si estaba asignado a otra venta (o ignorado), confirmar y MOVERLO.
    const pickCredit = (o: Row, creditId: number) => run(`o${o.id}`, async () => {
        const c = credits.find(x => x.id === creditId);
        if (!c) return;
        if (c.status === 'asignado' && c.order_id && c.order_id !== o.id) {
            const other = ordersById.get(c.order_id);
            if (!window.confirm(`Este crédito de ${fmtARS(c.amount)} del ${fmtDate(c.movement_date)} está asignado a ${other ? `${displayId(other)} ${other.contact_name || ''}` : `la orden ${c.order_id}`}.\n\n¿Lo pasás a ${displayId(o)} ${o.contact_name || ''}? La otra venta queda sin ese crédito.`)) return;
            const prev = c.order_id;
            await assign(o.id, c.id);
            if (!(creditsByOrder.get(prev) || []).some(x => x.id !== c.id) && !ordersById.get(prev)?.transfer_excluded_reason) {
                await supabase.from('orders').update({ transfer_verified: false, transfer_verified_at: null }).eq('id', prev);
                setOrderPatch(prev, { transfer_verified: false });
            }
            return;
        }
        if (c.status === 'ignorado' && !window.confirm(`Este crédito estaba marcado como "No es una venta". ¿Lo asignás a ${displayId(o)} ${o.contact_name || ''}?`)) return;
        await assign(o.id, c.id);
    });
    // selector manual: ventas (cualquier estado, también las que ya tienen crédito: pago en partes)
    const orderOptionsFor = (c: Credit) => orders
        .filter(o => !isPagoNube(o) && !o.transfer_excluded_reason)
        .map(o => ({ o, score: Math.abs(c.amount - o.total_price) / Math.max(1, o.total_price) + Math.abs(new Date(c.movement_date).getTime() - new Date(o.created_at).getTime()) / 86400000 / 60 }))
        .sort((a, b) => a.score - b.score).slice(0, 200)
        .map(({ o }) => ({
            value: o.id, label: `${displayId(o)} · ${o.contact_name || 'sin nombre'}`,
            sublabel: [fmtARS(o.total_price), fmtDate(o.created_at), o._bucket === 'efectivo' ? 'figura EFECTIVO' : null,
                o.payment_status !== 'paid' ? 'sin pagar' : null, o.status === 'cancelada' ? 'CANCELADA' : null,
                creditsByOrder.has(o.id) ? 'ya tiene crédito' : null, payerDoc(c.description) && payerDoc(c.description) === orderDoc(o) ? 'MISMO DNI' : null].filter(Boolean).join(' · '),
        }));

    // ── Columnas ──────────────────────────────────────────────────────────
    const faltanCols: GridCol<Row>[] = [
        { key: 'venta', header: 'Venta', sort: o => `${o.contact_name} ${displayId(o)}`, render: saleCell },
        { key: 'fecha', header: 'Fecha venta', sort: o => o.created_at, render: o => <span className="tabular-nums">{fmtDate(o.created_at)}</span> },
        {
            key: 'pago', header: 'Pago', sort: o => anchorOf(o), render: o => {
                const pi = payInfo(o);
                if (!pi) return <span className="text-[length:var(--text-xs)] text-faint" title="Sin comprobante y sin fecha de pago registrada">—</span>;
                const late = dayOf(pi.date) !== dayOf(o.created_at);
                return (
                    <span className="grid" title={pi.kind === 'comprobante' ? 'Cuando llegó el comprobante (WhatsApp o adjuntado a mano)' : 'Cuando se marcó la orden como pagada (no hay comprobante)'}>
                        <span className={cn('tabular-nums', late ? 'font-semibold text-ink' : 'text-soft')}>{fmtDate(pi.date)}</span>
                        <span className="text-[length:var(--text-xs)] text-faint">{pi.kind === 'comprobante' ? 'comprobante' : 'marcada paga'}</span>
                    </span>
                );
            },
        },
        { key: 'monto', header: 'Monto', align: 'right', sort: o => o.total_price, render: o => <span className="font-semibold tabular-nums">{fmtARS(o.total_price)}</span> },
        { key: 'comp', header: 'Comprobante', hideOnMobile: true, render: receiptBtn },
        {
            key: 'reenv', header: 'Reenviado', sort: o => o.transfer_resent_at || '', render: o => (
                <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap" title="Marcá cuando le reenviaste el comprobante a la financiera por WhatsApp">
                    <Checkbox aria-label="Reenviado a la financiera" checked={!!o.transfer_resent_at} disabled={busy === `r${o.id}`} onCheckedChange={() => toggleResent(o)} />
                    <span className={cn('text-[length:var(--text-xs)]', o.transfer_resent_at ? 'text-ink' : 'text-faint')}>
                        {o.transfer_resent_at ? `el ${fmtDate(o.transfer_resent_at)}` : 'no'}
                    </span>
                </label>
            ),
        },
        {
            key: 'credito', header: '¿Cuál es su crédito?', render: o => {
                const s = best.byOrder.get(o.id);
                const c = s ? credits.find(x => x.id === s.creditId) : null;
                return (
                    <div className="flex min-w-[260px] flex-wrap items-center gap-2">
                        {s && c ? (
                            <>
                                <span className="grid min-w-0">
                                    <span className="tabular-nums text-ink">{fmtARS(c.amount)} · {fmtDate(c.movement_date)}</span>
                                    <span className="text-[length:var(--text-xs)] text-soft">{KIND_LABEL[s.kind]}{s.kind !== 'exacto' ? ` · ${fmtDiff(s)}` : ''}{s.seña ? ' · seña' : ''}</span>
                                </span>
                                <Button size="sm" onClick={() => run(`o${o.id}`, () => assign(o.id, c.id))} disabled={busy === `o${o.id}`}><Check size={14} /> Es este</Button>
                            </>
                        ) : (
                            <PickerSelect className="min-w-[220px] flex-1" placeholder="Elegir crédito…" emptyText="No hay créditos libres"
                                value={null} options={creditOptionsFor(o)}
                                onChange={v => { if (v != null) pickCredit(o, Number(v)); }} />
                        )}
                    </div>
                );
            },
        },
        {
            key: 'acc', header: '', align: 'right', render: o => (
                <Button variant="ghost" size="sm" onClick={() => { setExcluding(o); setExReason(EXCLUDE_REASONS[0].value); setExNote(''); }}
                    title="Esta venta no pasa por la financiera (pagó a otra cuenta, en efectivo…)"><Ban size={14} /> No va a la financiera</Button>
            ),
        },
    ];

    const creditCols: GridCol<Credit>[] = [
        { key: 'fecha', header: 'Fecha', sort: c => c.movement_date, render: c => <span className="tabular-nums">{fmtDate(c.movement_date)}</span> },
        { key: 'monto', header: 'Monto', align: 'right', sort: c => c.amount, render: c => <span className="font-semibold tabular-nums">{fmtARS(c.amount)}</span> },
        {
            key: 'quien', header: 'Quién transfirió', sort: c => c.description || '', hideOnMobile: true, render: c => {
                const p = payerInfo(c.description);
                return p.cuit ? <span className="tabular-nums text-soft">{p.bank ? `${p.bank} · ` : ''}{p.cuit}</span>
                    : <span className="text-faint" title={c.description || undefined}>sin datos{p.bank ? ` · ${p.bank}` : ''}</span>;
            },
        },
        {
            key: 'venta', header: '¿De qué venta es?', render: c => {
                const s = best.byCredit.get(c.id);
                const o = s ? ordersById.get(s.orderId) : null;
                return (
                    <div className="flex min-w-[260px] flex-wrap items-center gap-2">
                        {s && o ? (
                            <>
                                <span className="grid min-w-0">
                                    <span className="truncate text-ink">{displayId(o)} · {o.contact_name || 'sin nombre'}</span>
                                    <span className="text-[length:var(--text-xs)] text-soft">
                                        {KIND_LABEL[s.kind]}{s.kind !== 'exacto' ? ` · ${fmtDiff(s)}` : ''}
                                        {o.payment_status !== 'paid' ? ' · figura sin pagar' : ''}{o.status === 'cancelada' ? ' · cancelada' : ''}{o._bucket === 'efectivo' ? ' · figura efectivo' : ''}
                                    </span>
                                </span>
                                <Button size="sm" onClick={() => run(`c${c.id}`, () => assign(o.id, c.id))} disabled={busy === `c${c.id}`}><Check size={14} /> Es esta</Button>
                            </>
                        ) : (
                            <PickerSelect className="min-w-[220px] flex-1" placeholder="Elegir venta…" value={null} options={orderOptionsFor(c)}
                                onChange={v => { if (v != null) run(`c${c.id}`, () => assign(Number(v), c.id)); }} />
                        )}
                    </div>
                );
            },
        },
        {
            key: 'acc', header: '', align: 'right', render: c => (
                <Button variant="ghost" size="sm" onClick={() => confirmIgnore(c)} disabled={busy === `c${c.id}`} title="No corresponde a una venta (aporte, devolución, otro ingreso)"><X size={14} /> No es una venta</Button>
            ),
        },
    ];

    const acreditadaCols: GridCol<Row>[] = [
        { key: 'venta', header: 'Venta', sort: o => `${o.contact_name} ${displayId(o)}`, render: saleCell },
        { key: 'fecha', header: 'Fecha', sort: o => o.created_at, render: o => <span className="tabular-nums">{fmtDate(o.created_at)}</span>, hideOnMobile: true },
        {
            key: 'pago', header: 'Pago', hideOnMobile: true, sort: o => anchorOf(o), render: o => {
                const pi = payInfo(o);
                if (!pi) return <span className="text-[length:var(--text-xs)] text-faint" title="Sin comprobante y sin fecha de pago registrada">—</span>;
                const late = dayOf(pi.date) !== dayOf(o.created_at);
                return (
                    <span className="grid" title={pi.kind === 'comprobante' ? 'Cuando llegó el comprobante (WhatsApp o adjuntado a mano)' : 'Cuando se marcó la orden como pagada (no hay comprobante)'}>
                        <span className={cn('tabular-nums', late ? 'font-semibold text-ink' : 'text-soft')}>{fmtDate(pi.date)}</span>
                        <span className="text-[length:var(--text-xs)] text-faint">{pi.kind === 'comprobante' ? 'comprobante' : 'marcada paga'}</span>
                    </span>
                );
            },
        },
        { key: 'monto', header: 'Venta', align: 'right', sort: o => o.total_price, render: o => <span className="tabular-nums">{fmtARS(o.total_price)}</span> },
        { key: 'comp', header: 'Comprobante', hideOnMobile: true, render: receiptBtn },
        {
            key: 'cred', header: 'Acreditado', align: 'right', sort: o => (creditsByOrder.get(o.id) || []).reduce((s, c) => s + c.amount, 0), render: o => {
                const cs = creditsByOrder.get(o.id) || [];
                const tot = cs.reduce((s, c) => s + c.amount, 0);
                const off = Math.abs(tot - o.total_price) > Math.max(1, o.total_price * 0.02);
                return (
                    <span className="grid justify-items-end">
                        <span className={cn('font-semibold tabular-nums', off ? 'text-[var(--warning)]' : 'text-ink')}>{fmtARS(tot)}</span>
                        <span className="text-[length:var(--text-xs)] text-soft">{cs.length > 1 ? `${cs.length} créditos` : fmtDate(cs[0].movement_date)}{off ? ` · ${tot > o.total_price ? '+' : '−'}${fmtARS(Math.abs(tot - o.total_price))}` : ''}</span>
                    </span>
                );
            },
        },
        {
            key: 'titular', header: 'Titular', hideOnMobile: true, render: o => {
                const doc = orderDoc(o);
                const payer = (creditsByOrder.get(o.id) || []).map(c => payerDoc(c.description)).find(Boolean);
                if (!doc || !payer) return <span className="text-faint">—</span>;
                return payer === doc ? <Badge size="sm" tone="success" icon={<UserCheck size={12} />}>mismo DNI</Badge>
                    : <Badge size="sm" title="Transfirió otra persona (pareja, familiar) o el crédito no es de esta venta">otro titular</Badge>;
            },
        },
        {
            key: 'acc', header: '', align: 'right', render: o => (
                <Button variant="ghost" size="sm" disabled={!!busy} title="Desasignar el crédito (vuelve a la lista)"
                    onClick={() => (creditsByOrder.get(o.id) || []).forEach(c => unassign(c))}><Undo2 size={14} /> Deshacer</Button>
            ),
        },
    ];

    const fueraCols: GridCol<Row>[] = [
        { key: 'venta', header: 'Venta', sort: o => `${o.contact_name} ${displayId(o)}`, render: saleCell },
        { key: 'fecha', header: 'Fecha', sort: o => o.created_at, render: o => <span className="tabular-nums">{fmtDate(o.created_at)}</span>, hideOnMobile: true },
        { key: 'monto', header: 'Monto', align: 'right', sort: o => o.total_price, render: o => <span className="tabular-nums">{fmtARS(o.total_price)}</span> },
        { key: 'motivo', header: 'Motivo', sort: o => o.transfer_excluded_reason || KRILIC_REASON, render: o => <span className="text-soft">{o.transfer_excluded_reason || KRILIC_REASON}</span> },
        {
            key: 'acc', header: '', align: 'right', render: o => o.transfer_excluded_reason
                ? <Button variant="ghost" size="sm" onClick={() => unexclude(o)} disabled={busy === `o${o.id}`}><Undo2 size={14} /> Deshacer</Button>
                : <span className="text-[length:var(--text-xs)] text-faint" title="Se cambia editando la venta">elegido en la venta</span>,
        },
    ];
    const ignoredCols: GridCol<Credit>[] = [
        { key: 'fecha', header: 'Fecha', sort: c => c.movement_date, render: c => <span className="tabular-nums">{fmtDate(c.movement_date)}</span> },
        { key: 'monto', header: 'Monto', align: 'right', sort: c => c.amount, render: c => <span className="tabular-nums">{fmtARS(c.amount)}</span> },
        { key: 'desc', header: 'Descripción', sort: c => c.description || '', render: c => <span className="text-soft">{c.description}</span> },
        { key: 'acc', header: '', align: 'right', render: c => <Button variant="ghost" size="sm" onClick={() => ignore(c, true)} disabled={busy === `c${c.id}`}><Undo2 size={14} /> Deshacer</Button> },
    ];

    const tabs = [
        { value: 'faltan', label: 'Faltan en la cuenta', accessory: <>
            <Badge size="sm" tone={groups.falta.length ? 'danger' : 'success'}>{groups.falta.length}</Badge>
            {groups.esperando.length > 0 && <Badge size="sm" tone="info" title="Próximo cierre: pagadas después del estado de cuenta cargado">+{groups.esperando.length}</Badge>}
        </> },
        { value: 'creditos', label: 'Créditos sin venta', accessory: freeInMonth.length ? <Badge size="sm" tone="warning">{freeInMonth.length}</Badge> : undefined },
        { value: 'acreditadas', label: 'Acreditadas', accessory: <Badge size="sm">{groups.acreditada.length}</Badge> },
        { value: 'fuera', label: 'Fuera del control', accessory: (groups.fuera.length + ignoredCredits.length) ? <Badge size="sm">{groups.fuera.length + ignoredCredits.length}</Badge> : undefined },
    ];
    const toLoad = parsed.filter(p => p.include).length;
    const toEnrich = parsed.filter(p => p.enrich).length;

    return (
        <div className="tw w-full min-w-0 pb-12 pt-1">
            <PageHeader
                title="Transferencias"
                description="Cada venta cobrada por transferencia tiene que aparecer como crédito en el estado de cuenta de la financiera. Acá ves cuáles faltan."
                actions={<Button onClick={() => fileRef.current?.click()} loading={parsing}><Upload size={16} /> Subir estado de cuenta</Button>}
            />

            {/* ── Qué cubre el estado de cuenta ───────────────────────────── */}
            <input ref={fileRef} type="file" accept="image/*,application/pdf,.pdf" multiple className="hidden"
                onChange={e => { onFiles(e.target.files); e.target.value = ''; }} />
            <section
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); if (!parsing) onFiles(e.dataTransfer.files); }}
                className={cn('mb-5 flex flex-wrap items-center gap-3 rounded-[var(--radius-surface)] border border-dashed bg-card p-4 shadow-xs transition-colors',
                    dragOver ? 'border-[var(--foreground)]' : 'border-[var(--border-strong)]')}>
                <span className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-muted text-soft">
                    {parsing ? <Loader2 size={18} className="animate-spin" /> : <Landmark size={18} />}
                </span>
                <div className="min-w-[220px] flex-1">
                    <div className="text-[length:var(--text-sm)] font-semibold text-ink">
                        {parsing ? 'Leyendo el estado de cuenta…'
                            : cover ? `Estado de cuenta cargado del ${fmtDate(cover.start)} al ${fmtDate(cover.end)}`
                            : 'Todavía no hay ningún estado de cuenta cargado'}
                    </div>
                    <div className="text-[length:var(--text-sm)] text-soft">
                        {cover && groups.esperando.length > 0
                            ? `${groups.esperando.length} venta${groups.esperando.length === 1 ? '' : 's'} posterior${groups.esperando.length === 1 ? '' : 'es'} (${fmtARS(sum(groups.esperando))}) esperan el próximo. `
                            : ''}
                        Arrastrá acá el PDF que manda la financiera. Solo se toman los créditos; lo que ya estaba cargado se saltea solo.
                    </div>
                </div>
            </section>

            {notice && <div className="mb-5"><Notice tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Notice></div>}

            {/* ── Revisión de lo leído ────────────────────────────────────── */}
            {parsed.length > 0 && (
                <Panel className="mb-5" title={`Créditos leídos (${parsed.length})`}
                    right={<span className="text-[length:var(--text-sm)] text-soft">{toLoad} nuevos{toEnrich ? ` · ${toEnrich} completan datos` : ''}{parsed.length - toLoad - toEnrich ? ` · ${parsed.length - toLoad - toEnrich} ya cargados` : ''}</span>}>
                    <div className="max-h-[45vh] overflow-auto rounded-[var(--radius-control)] border border-line">
                        <table className="w-full border-collapse text-[length:var(--text-sm)]">
                            <tbody>
                                {parsed.map((p, i) => (
                                    <tr key={i} className={cn('border-b border-line last:border-0', !p.include && 'opacity-50')}>
                                        <td className="w-10 px-3 py-1.5">
                                            <Checkbox aria-label="Cargar" checked={p.include} onCheckedChange={() => setParsed(ps => ps.map((x, j) => j === i ? { ...x, include: !x.include } : x))} />
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{fmtDate(p.date)}</td>
                                        <td className="whitespace-nowrap px-3 py-1.5 text-right font-semibold tabular-nums">{fmtARS(p.amount)}</td>
                                        <td className="px-3 py-1.5 text-soft">
                                            {p.enrich ? <Badge size="sm" tone="success" className="mr-1.5" title="Este crédito ya estaba cargado sin CUIT: se le completa la descripción">completa datos</Badge>
                                                : p.dup && <Badge size="sm" className="mr-1.5" title="Ya está cargado (misma fecha y monto). Tildalo solo si es OTRA transferencia idéntica.">ya cargado</Badge>}
                                            {p.description}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button onClick={loadParsed} loading={saving} disabled={!toLoad && !toEnrich}>
                            {toLoad ? `Cargar ${toLoad} crédito${toLoad === 1 ? '' : 's'}` : ''}{toLoad && toEnrich ? ' y ' : ''}{toEnrich ? `${toLoad ? 'c' : 'C'}ompletar ${toEnrich}` : ''}
                        </Button>
                        <Button variant="ghost" onClick={() => setParsed([])} disabled={saving}>Descartar</Button>
                    </div>
                </Panel>
            )}

            {/* ── Período ─────────────────────────────────────────────────── */}
            {months.length > 1 && (
                <div className="mb-4 min-w-0">
                    <SegmentedControl label="Mes" value={month} onValueChange={setMonth}
                        options={[{ value: 'all', label: 'Todo' }, ...months.map(m => ({ value: m, label: monthLabel(m) }))]} />
                </div>
            )}

            {/* ── Resumen ─────────────────────────────────────────────────── */}
            <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <StatCard label="Cobradas por transferencia" value={controlled.toLocaleString('es-AR')}
                    context={`${fmtARS(sum(groups.acreditada) + sum(groups.falta))} en el período del estado de cuenta`} />
                <StatCard label="Acreditadas" value={`${pctOk}%`} tone={pctOk >= 98 ? 'success' : undefined}
                    context={`${groups.acreditada.length} de ${controlled} aparecen en la cuenta`} />
                <StatCard label="Faltan en la cuenta" value={groups.falta.length.toLocaleString('es-AR')} tone={groups.falta.length ? 'danger' : 'success'}
                    context={groups.falta.length ? `${fmtARS(sum(groups.falta))} sin acreditar` : 'todas acreditadas'} />
                <StatCard label="Créditos sin venta" value={freeInMonth.length.toLocaleString('es-AR')} tone={freeInMonth.length ? 'warning' : undefined}
                    context={freeInMonth.length ? `${fmtARS(freeInMonth.reduce((s, c) => s + c.amount, 0))} que entraron sin venta asignada` : 'todos asignados'} />
            </section>

            {safeInMonth.length > 0 && (
                <div className="mb-5">
                    <Notice tone="success" action={<Button size="sm" onClick={() => confirmSafe(safeInMonth)} loading={busy === 'safe'}><CheckCheck size={14} /> Confirmar {safeInMonth.length}</Button>}>
                        Hay {safeInMonth.length} cruce{safeInMonth.length === 1 ? '' : 's'} seguro{safeInMonth.length === 1 ? '' : 's'}: coincide con el comprobante leído, mismo DNI que el cliente, o un monto exacto que no se repite.
                    </Notice>
                </div>
            )}

            <div className="mb-4 min-w-0">
                <SegmentedControl label="Vista" value={tab} onValueChange={v => setTab(v as Tab)} options={tabs} />
            </div>

            {tab === 'faltan' && (
                groups.falta.length === 0 && groups.esperando.length === 0 ? (
                    <div className="rounded-[var(--radius-surface)] border border-line bg-card">
                        <EmptyState icon={<CheckCircle2 size={20} />} title="No falta ninguna"
                            description={cover ? `Todas las transferencias hasta el ${fmtDate(cover.limit)} aparecen en el estado de cuenta.` : 'Subí un estado de cuenta para empezar.'} />
                    </div>
                ) : (
                    <DataGrid rows={[...groups.falta, ...groups.esperando]} cols={faltanCols} searchPlaceholder="Buscar cliente, pedido o monto…"
                        title={<span className="text-[length:var(--text-sm)] text-soft">Pagadas por transferencia, sin crédito en la cuenta. Si el comprobante está bien, reclamalas a la financiera.{groups.esperando.length ? ` Al final, ${groups.esperando.length} con PRÓXIMO CIERRE: pagadas después del ${cover ? fmtDate(cover.limit) : ''}, se controlan con el próximo estado de cuenta.` : ''}{groups.falta.some(o => o.transfer_resent_at) ? ` ${groups.falta.filter(o => o.transfer_resent_at).length} ya reenviada${groups.falta.filter(o => o.transfer_resent_at).length === 1 ? '' : 's'}.` : ''}</span>}
                        toolbar={<Button variant="secondary" size="sm" onClick={copyClaim}><Copy size={14} /> Copiar para reclamar</Button>} />
                )
            )}

            {tab === 'creditos' && (
                freeInMonth.length === 0 ? (
                    <div className="rounded-[var(--radius-surface)] border border-line bg-card">
                        <EmptyState icon={<CheckCircle2 size={20} />} title="Todos los créditos tienen su venta" description="Cada crédito del estado de cuenta está asignado a una venta o marcado como que no es una venta." />
                    </div>
                ) : (
                    <DataGrid rows={freeInMonth} cols={creditCols} searchPlaceholder="Buscar monto o CUIT…"
                        title={<span className="text-[length:var(--text-sm)] text-soft">Plata que entró y no está asignada a ninguna venta.</span>} />
                )
            )}

            {tab === 'acreditadas' && (
                <DataGrid rows={groups.acreditada} cols={acreditadaCols} searchPlaceholder="Buscar cliente o pedido…"
                    emptyTitle="Todavía no hay ventas acreditadas" />
            )}

            {tab === 'fuera' && (
                <div className="grid gap-5">
                    {pagoNubeCount > 0 && (
                        <Notice tone="info">{pagoNubeCount} venta{pagoNubeCount === 1 ? '' : 's'} con “Pago Nube – transferencia” no se controla{pagoNubeCount === 1 ? '' : 'n'} acá: esa plata la cobra Pago Nube, no la financiera.</Notice>
                    )}
                    <DataGrid rows={groups.fuera} cols={fueraCols} title={<span className="font-semibold text-ink">Ventas que no pasan por la financiera</span>}
                        emptyTitle="Ninguna" emptyText="Desde “Faltan en la cuenta” podés sacar del control una venta que se pagó por otro lado." />
                    <DataGrid rows={ignoredCredits} cols={ignoredCols} title={<span className="font-semibold text-ink">Créditos que no son ventas</span>}
                        emptyTitle="Ninguno" />
                </div>
            )}

            <OrderPeek orderId={peek?.id ?? null} receipts={peek ? receiptsFor(peek) : undefined}
                onClose={() => setPeek(null)} onEdited={() => fetchAll()} />

            {/* ── "No va a la financiera" ─────────────────────────────────── */}
            <Dialog open={!!excluding} onOpenChange={o => { if (!o) setExcluding(null); }}>
                <DialogContent title="Sacar del control" className="w-[min(520px,calc(100vw-32px))]"
                    description={excluding ? `${displayId(excluding)} · ${excluding.contact_name || ''} · ${fmtARS(excluding.total_price)}. Deja de figurar como faltante; se puede deshacer desde “Fuera del control”.` : ''}>
                    <div className="grid gap-4">
                        <ChipGroup label="Motivo" multiple={false} options={EXCLUDE_REASONS} value={[exReason]} onValueChange={v => { if (v[0]) setExReason(v[0]); }} />
                        <Textarea label={exReason === 'Otro' ? 'Contá qué pasó' : 'Nota (opcional)'} rows={2} value={exNote} onChange={e => setExNote(e.target.value)} />
                        <div className="flex justify-end gap-2 border-t border-line pt-4">
                            <Button variant="secondary" onClick={() => setExcluding(null)}>Cancelar</Button>
                            <Button onClick={saveExclude} loading={!!excluding && busy === `o${excluding.id}`} disabled={exReason === 'Otro' && !exNote.trim()}><Ban size={16} /> Sacar del control</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
